import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { ApiUrl } from "Services/Services";
import { CgProfile } from "react-icons/cg";
import { FiMenu, FiX } from "react-icons/fi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader, BeatLoader, PuffLoader } from "react-spinners";
import { formatToPakistaniCurrency } from "Services/Services";

export default function POSScreen() {
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [gridCols, setGridCols] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cartModalOpen, setCartModalOpen] = useState(false);

  const [paymentMode, setPaymentMode] = useState("Cash");
  const [discount, setDiscount] = useState(0);
  const [mobile, setMobile] = useState("");
  const [orderType, setOrderType] = useState("pickup");
  const [email, setEmail] = useState("");

  // modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [toppings, setToppings] = useState([]);
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);

  // Loading states
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingTables, setLoadingTables] = useState(false);
  const [loadingToppings, setLoadingToppings] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);

  useEffect(() => {
    if (orderType === "dining") fetchTables();
  }, [orderType]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await axios.get(`${ApiUrl}/category`);
      const cats = res.data.data || [];
      setCategories([{ id: "all", title: "All" }, ...cats, { id: "deal", title: "Deal" }]);
    } catch (err) {
      console.error("Error fetching categories", err);
      toast.error("Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchProducts = async (categoryId = null) => {
    setLoadingProducts(true);
    try {
      let res;
      if (!categoryId || categoryId === "all") {
        res = (await axios.get(`${ApiUrl}/food`)).data;
        setProducts(res.foods || []);
      } else if (categoryId === "deal") {
        res = (await axios.get(`${ApiUrl}/deals`)).data;
        setProducts(res.data || []);
      } else {
        res = (await axios.get(`${ApiUrl}/category/${categoryId}`)).data.category;
        setProducts(res.foods || []);
      }
    } catch (err) {
      console.error("Error fetching products", err);
      toast.error("Failed to load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchTables = async () => {
    setLoadingTables(true);
    try {
      const res = await axios.get(`${ApiUrl}/table`);
      setTables(res.data.data || []);
    } catch (err) {
      console.error("Error fetching tables", err);
      toast.error("Failed to load tables");
    } finally {
      setLoadingTables(false);
    }
  };

  const fetchToppingsByCategory = async (categoryId) => {
    setLoadingToppings(true);
    try {
      const res = await axios.get(`${ApiUrl}/topping/category/${categoryId}`);
      setToppings(res.data.data || []);
    } catch (err) {
      console.error("Error fetching toppings", err);
      toast.error("Failed to load toppings");
    } finally {
      setLoadingToppings(false);
    }
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setSelectedVariation(null);
    setSelectedToppings([]);
    if (product.categoryId) fetchToppingsByCategory(product.categoryId);
    setModalOpen(true);
  };

  const total = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum +
          (parseFloat(item.price) +
            item.toppings.reduce((a, t) => a + t.price * t.qty, 0)) *
            item.qty,
        0
      ),
    [cart]
  );

  const discountedTotal = useMemo(() => {
    return total - (total * discount) / 100;
  }, [total, discount]);

  const addToCart = () => {
    if (!selectedProduct) return;

    // ✅ Handle Deal
    if (selectedCategory === "deal") {
      const newCartItem = {
        id: `deal-${selectedProduct.id}`,
        productId: selectedProduct.id,
        name: selectedProduct.name,
        price: selectedProduct.price,
        qty: 1,
        toppings: [],
        isDeal: true, // ✅ flag as deal
      };

      setCart((prev) => {
        const existing = prev.find((i) => i.id === newCartItem.id);
        if (existing) {
          return prev.map((i) =>
            i.id === newCartItem.id ? { ...i, qty: i.qty + 1 } : i
          );
        } else {
          return [...prev, newCartItem];
        }
      });

      setSelectedProduct(null);
      setSelectedVariation(null);
      setSelectedToppings([]);
      setModalOpen(false);
      return;
    }

    // ✅ Handle Normal Food
    if (selectedProduct.variations?.length > 0 && !selectedVariation) {
      alert("Please select a variation.");
      return;
    }

    const variationLabel = selectedVariation?.name || "Default";
    const variationPrice = selectedVariation?.price || selectedProduct.price || 0;

    const toppingsTotal = selectedToppings.reduce(
      (sum, t) => sum + (t.price || 0) * (t.qty || 1),
      0
    );

    const newCartItem = {
      id: `${selectedProduct.id}-${variationLabel}-${selectedToppings
        .map((t) => t.id + "_" + t.qty)
        .join(",")}`,
      productId: selectedProduct.id,
      selectedVariationId: selectedVariation?.id || null,
      name: selectedProduct.name,
      variation: variationLabel,
      price: variationPrice,
      toppings: selectedToppings,
      toppingsTotal,
      qty: 1,
      isDeal: false,
    };

    setCart((prev) => {
      const existing = prev.find((i) => i.id === newCartItem.id);
      if (existing) {
        return prev.map((i) =>
          i.id === newCartItem.id ? { ...i, qty: i.qty + 1 } : i
        );
      } else {
        return [...prev, newCartItem];
      }
    });

    setSelectedProduct(null);
    setSelectedVariation(null);
    setSelectedToppings([]);
    setModalOpen(false);
  };

  const removeFromCart = (id) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const clearCart = () => {
    setCart([]);
  };

  const placeOrder = async () => {
    let newErrors = {};
    if (!mobile.trim()) newErrors.mobile = "Phone number is required";
    if (!email.trim()) newErrors.email = "Name is required";
    if (!orderType.trim()) newErrors.orderType = "Order type is required";
    if (orderType === "dining" && !selectedTable) {
      newErrors.table = "Please select a table for dining order";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setPlacingOrder(true);

    try {
      const mappedItems = cart.map((item) => {
        if (item.isDeal) {
          return {
            dealId: item.productId,
            quantity: item.qty,
          };
        } else {
          return {
            foodId: item.productId,
            variationId: item.selectedVariationId,
            quantity: item.qty,
            toppings: item.toppings.map((t) => ({
              toppingId: t.id,
              quantity: t.qty || 1,
            })),
          };
        }
      });

      const orderData = {
        organizationId: 2,
        branchId: 1,
        orderType,
        tableId: orderType === "dining" ? selectedTable : null,
        phone: mobile,
        name: email,
        items: mappedItems,
        notes: "",
      };

      console.log("Submitting order:", orderData);
      await axios.post(`${ApiUrl}/order/create`, orderData);

      setCartModalOpen(false);
      clearCart();
      toast.success("Order placed successfully!");
    } catch (err) {
      console.error("Error placing order:", err);
      toast.error("Failed to place order");
    } finally {
      setPlacingOrder(false);
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-white border-b shadow-sm">
        <div className="text-[22px] sm:text-[26px] font-bold uppercase flex items-center gap-3">
          <button
            className="xl:hidden p-2 rounded hover:bg-gray-100"
            onClick={() => setSidebarOpen(true)}
          >
            <FiMenu size={22} />
          </button>
          Vegan <span className="font-medium">Foods</span>
        </div>
        <div className="flex items-center space-x-2 text-sm sm:text-base">
          <span className="font-medium">John Doe</span>
          <CgProfile size={23} />
        </div>
      </div>

      <div className="flex bg-gray-100" style={{ height: "calc(100vh - 72px)" }}>
        {/* Sidebar */}
        <div className="hidden xl:block w-[260px] bg-white border-r p-4 overflow-y-auto">
          <h2 className="text-lg font-semibold mb-4">Categories</h2>
          {loadingCategories ? (
            <div className="flex justify-center py-4">
              <ClipLoader size={20} color="#3B82F6" />
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    fetchProducts(cat.id);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg transition ${
                    selectedCategory === cat.id
                      ? "bg-blue-600 text-white shadow"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="p-3 flex flex-wrap gap-3 items-center bg-white border-b shadow-sm">
            <input
              type="text"
              placeholder="Search Dish"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 min-w-[140px] border rounded-lg px-3 py-2 focus:ring focus:ring-blue-200"
            />
            <select
              className="border rounded-lg px-3 py-2 focus:ring min-w-[120px]"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
            >
              <option>pickup</option>
              <option>dining</option>
            </select>
            <select
              className="border rounded-lg px-3 py-2 focus:ring min-w-[160px]"
              value={gridCols}
              onChange={(e) => setGridCols(Number(e.target.value))}
            >
              <option value={2}>2 Dishes Per Row</option>
              <option value={3}>3 Dishes Per Row</option>
              <option value={4}>4 Dishes Per Row</option>
            </select>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            {loadingProducts ? (
              <div className="flex justify-center items-center h-full">
                <BeatLoader size={15} color="#3B82F6" />
              </div>
            ) : (
              <div
                className="grid gap-3"
                style={{
                  gridTemplateColumns: `repeat(${gridCols}, minmax(160px, 1fr))`,
                }}
              >
                {products
                  .filter((p) =>
                    p.name?.toLowerCase().includes(search.toLowerCase())
                  )
                  .map((product) => (
                    <div
                      key={product.id}
                      className="border rounded-lg bg-white shadow hover:shadow-md transition p-2 flex flex-col cursor-pointer"
                      onClick={() => openProductModal(product)}
                    >
                      <img
                        src={product.image || "/no-image.png"}
                        alt={product.name}
                        className="h-28 object-cover rounded-lg mb-2"
                        loading="lazy"
                      />
                      <p className="font-medium truncate">{product.name}</p>
                      <p className="text-blue-600 font-semibold">
                         {formatToPakistaniCurrency(product.price)}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Cart */}
        <div className="w-[260px] md:w-[340px] bg-white border-l flex flex-col">
          <div className="flex-1 p-4 overflow-y-auto">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <svg
                  className="w-16 h-16 mb-2"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M3 3h2l.4 2M7 13h14l-1.5 8H6.1L5 6h16l1 7H7z"></path>
                </svg>
                Empty Cart
              </div>
            ) : (
              cart.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between items-center mb-3 border-b pb-2"
                >
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      {item.variation} | {item.qty} x 
                      {(parseFloat(item.price) +
                        item.toppings.reduce((a, t) => a + t.price * t.qty, 0)
                      ).toFixed(2)}
                    </p>
                    {item.toppings.length > 0 && (
                      <ul className="text-xs text-gray-400">
                        {item.toppings.map((t) => (
                          <li key={t.id}>
                            + {t.name} (x{t.qty || 1})
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold">
                      {(
                        (parseFloat(item.price) +
                          item.toppings.reduce((a, t) => a + t.price * t.qty, 0)) *
                        item.qty
                      ).toFixed(2)}
                    </p>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t bg-gray-50">
            <p className="font-semibold mb-2 text-gray-700">
              Total: {formatToPakistaniCurrency(total.toFixed(2))}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCartModalOpen(true)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 transition"
              >
                Submit
              </button>
              <button
                onClick={clearCart}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg py-2 transition"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Modal */}
      {modalOpen && selectedProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[420px] rounded-xl shadow-lg p-5 relative">
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-black"
            >
              <FiX size={22} />
            </button>
            <h2 className="text-xl font-semibold mb-3">
              {selectedProduct.name}
            </h2>
            <img
              src={selectedProduct.image || "/no-image.png"}
              alt={selectedProduct.name}
              className="h-40 w-full object-cover rounded mb-3"
            />

            {/* Variations */}
            {selectedProduct.variations?.length > 0 && (
              <div className="mb-3">
                <p className="font-medium mb-1">Choose Size:</p>
                <div className="flex gap-2 flex-wrap">
                  {selectedProduct.variations.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setSelectedVariation(v)}
                      className={`px-3 py-1 border rounded ${
                        selectedVariation?.id === v.id
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100"
                      }`}
                    >
                      {v.name} - {formatToPakistaniCurrency(v.price)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Toppings with qty */}
            {toppings.length > 0 && (
              <div className="mb-3">
                <p className="font-medium mb-1">Choose Toppings:</p>
                {loadingToppings ? (
                  <div className="flex justify-center py-2">
                    <ClipLoader size={15} color="#3B82F6" />
                  </div>
                ) : (
                  <div className="space-y-1">
                    {toppings.map((t) => (
                      <div key={t.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedToppings.some((tp) => tp.id === t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedToppings([...selectedToppings, { ...t, qty: 1 }]);
                            } else {
                              setSelectedToppings(selectedToppings.filter((tp) => tp.id !== t.id));
                            }
                          }}
                        />
                        <span className="flex-1">
                          {t.name} - {formatToPakistaniCurrency(t.price)}
                        </span>
                        {selectedToppings.some((tp) => tp.id === t.id) && (
                          <input
                            type="number"
                            min={1}
                            value={selectedToppings.find((tp) => tp.id === t.id).qty}
                            onChange={(e) => {
                              setSelectedToppings(
                                selectedToppings.map((tp) =>
                                  tp.id === t.id ? { ...tp, qty: Number(e.target.value) } : tp
                                )
                              );
                            }}
                            className="w-12 border rounded px-1 py-1 text-sm"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={addToCart}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2 mt-4"
            >
              Add to Cart
            </button>
          </div>
        </div>
      )}

      {/* Submit Cart Modal */}
      {cartModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white w-[450px] rounded-lg shadow-lg p-5 relative">
            <button
              onClick={() => setCartModalOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-black"
            >
              <FiX size={22} />
            </button>
            <h2 className="text-lg font-semibold mb-4 text-center">
              Cart Summary
            </h2>

            <div className="space-y-3 mb-4 max-h-[250px] overflow-y-auto">
              {cart.map((item) => (
                <div key={item.id} className="border-b pb-2">
                  <div className="flex justify-between text-sm font-medium">
                    <span>
                      {item.name} ({item.variation}) x {item.qty}
                    </span>
                    <span>
                      
                      {(
                        (parseFloat(item.price) +
                          item.toppings.reduce((a, t) => a + t.price * t.qty, 0)) *
                        item.qty
                      ).toFixed(2)}
                    </span>
                  </div>
                  {item.toppings.length > 0 && (
                    <ul className="text-xs text-gray-500 ml-2">
                      {item.toppings.map((t) => (
                        <li key={t.id}>
                          + {t.name} x{t.qty}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            <div className="mb-3 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm font-medium">Total:</span>
                <span>{formatToPakistaniCurrency(total.toFixed(2))}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Discount:</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value))}
                  className="w-20 border rounded px-1 py-1 text-sm"
                />
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Final Total:</span>
                <span>{formatToPakistaniCurrency(discountedTotal.toFixed(2))}</span>
              </div>
            </div>

            <div className="space-y-2 mb-4">
              <label className="block text-sm font-medium  mb-0">Name</label>
              <input
                type="text"
                placeholder="Name"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              {errors.email && (
                <p className="text-red-500 text-xs">{errors.email}</p>
              )}
            <label className="block text-sm font-medium  mb-0">Phone</label>
              <input
                type="text"
                placeholder="Mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
              />
              {errors.mobile && (
                <p className="text-red-500 text-xs">{errors.mobile}</p>
              )}
              
              {orderType === "dining" && (
                <>
                  {loadingTables ? (
                    <div className="flex justify-center py-2">
                      <ClipLoader size={15} color="#3B82F6" />
                    </div>
                  ) : (
                    <select
                      value={selectedTable || ""}
                      onChange={(e) => setSelectedTable(e.target.value)}
                      className="w-full border rounded px-3 py-2 text-sm"
                    >
                      <option value="">Select Table</option>
                      {tables.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.number}
                        </option>
                      ))}
                    </select>
                  )}
                </>
              )}
            </div>

            <button
              onClick={placeOrder}
              disabled={placingOrder}
              className="w-full bg-green-600 hover:bg-green-700 text-white rounded-lg py-2 flex items-center justify-center"
            >
              {placingOrder ? (
                <PuffLoader size={20} color="#fff" />
              ) : (
                "Place Order"
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
}